import { BiddingApprovalState } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

/**
 * 承認状態バナーの優先度 (spec `spot-bidding.md` AC-BSP-082、 issue #5147)。
 *
 * AC-BSP-082 は同一受渡日に複数の承認案件が並存することを許可し、
 * 「混在する場合の見せ方は **Des 判断範囲**」 と定める。 見せ方は
 * **優先度の高い順に 3 件まで大バナーで並べ、 4 件目以降だけ「残り N 件を表示」 で畳む**
 * (2026-08-26 Des 判断。 [[ApprovalBannerStack]] の `VISIBLE_LIMIT`。 8080 `bidding/spot.html`
 * の `_renderSpotMixedApproval` も同じ)。 本ファイルはその並び順を決める。
 *
 * 旧: 2026-07-23 Des 判断 (案B) の「1 件だけ大バナー + 残りはステータス単位のチップに畳む」。
 * 畳んだ状態を既定にすると開く導線に気づけない、 というデザイナー指摘で 08-26 に置き換えた
 * (チップと件数の要約も撤去。 #6562 で確定)。
 */

/**
 * 大バナーに出す 1 件を選ぶ優先度 (小さいほど優先)。
 *
 * design `_MX_PRIORITY` (差戻し > 承認申請中 > 入札中) の移植。
 * 「人が次に手を動かすべきもの」 を前に出す、 という基準で並べる:
 * 差戻しは再編集が必要 → 申請中は承認待ちで見守る → 承認済/提出済は完了側。
 *
 * design は受渡日あたり 1 状態しか持たないため APPROVED と SUBMITTED を
 * `approved` に畳んでいるが、 実装は区別できるので順序だけ分ける
 * (承認済 = 提出に進む前の過渡状態 → 提出済 = 完了、 の順)。
 *
 * REOPEN 後 DRAFT (= 差戻し履歴を持つ下書き。 AC-BSP-056) は design に対応する
 * 状態が無い。 「再申請前の作業中」 = 差戻しの次に手を動かす対象なので、
 * 差戻しの直後に置く。
 */
const PRIORITY: Partial<Record<BiddingApprovalState, number>> = {
  [BiddingApprovalState.REJECTED]: 0,
  [BiddingApprovalState.DRAFT]: 1,
  [BiddingApprovalState.PENDING]: 2,
  [BiddingApprovalState.APPROVED]: 3,
  [BiddingApprovalState.SUBMITTED]: 4,
}

/** 優先度未定義 (= バナーを出さない状態) は末尾に送る。 */
const LOWEST = Number.MAX_SAFE_INTEGER

export function approvalBannerPriority(state: BiddingApprovalState): number {
  return PRIORITY[state] ?? LOWEST
}

/**
 * バナー対象を優先度順に並べ替える (安定ソート)。
 *
 * 同じ優先度の中では**渡された順を保つ**。 呼び出し元の一覧は backend の
 * `ListBiddingSpotApprovalRequests` (created_at DESC) 由来なので、 同状態が
 * 複数ある時は新しい申請セットが先に来る。
 */
export function sortByApprovalBannerPriority<T>(
  items: readonly T[],
  stateOf: (item: T) => BiddingApprovalState,
): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) =>
        approvalBannerPriority(stateOf(a.item)) -
          approvalBannerPriority(stateOf(b.item)) || a.index - b.index,
    )
    .map(({ item }) => item)
}
